import { useEffect, useRef, useState } from 'react'

import type { ConversationTurnEntry } from '@/index'
import type { FormattedAgentActivity } from '@/features/events/activity/utils/types'

import type { DemoActivityStep } from '@/features/home/demo/data'
import { DEMO_SPEED_MULTIPLIER } from '@/features/home/demo/data'

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

interface UseDemoActivityPlaybackOptions {
  readonly script: readonly DemoActivityStep[]
  readonly isVisible: boolean
}

interface UseDemoActivityPlaybackReturn {
  readonly currentActivity: FormattedAgentActivity | null
  readonly currentEvent: ConversationTurnEntry | null
}

export function useDemoActivityPlayback(
  options: UseDemoActivityPlaybackOptions,
): UseDemoActivityPlaybackReturn {
  const { script, isVisible } = options

  const [stepIndex, setStepIndex] = useState(0)
  const [currentEventIndex, setCurrentEventIndex] = useState<number | null>(null)
  const previousVisibleRef = useRef(isVisible)

  const prefersReducedMotion = useRef(
    'matchMedia' in globalThis && globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches,
  )

  // Reset to step 0 when visibility transitions from false -> true
  useEffect(() => {
    const wasVisible = previousVisibleRef.current
    previousVisibleRef.current = isVisible

    if (!wasVisible && isVisible) {
      setStepIndex(0)
      setCurrentEventIndex(null)
    }
  }, [isVisible])

  // Advance through script steps on a timer
  useEffect(() => {
    if (prefersReducedMotion.current) return
    if (!isVisible) return
    if (script.length === 0) return

    const currentStep = script[stepIndex]
    if (!currentStep) return

    const timeoutId = setTimeout(() => {
      const nextIndex = (stepIndex + 1) % script.length
      const nextStep = script[nextIndex]
      if (nextStep?.eventOverride) {
        setCurrentEventIndex(nextIndex)
      }
      setStepIndex(nextIndex)
    }, currentStep.durationMs * DEMO_SPEED_MULTIPLIER)

    return () => clearTimeout(timeoutId)
  }, [isVisible, stepIndex, script])

  return {
    currentActivity: resolveCurrentActivity(script, stepIndex, prefersReducedMotion.current),
    currentEvent: resolveCurrentEvent(script, currentEventIndex, prefersReducedMotion.current),
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function resolveCurrentActivity(
  script: readonly DemoActivityStep[],
  stepIndex: number,
  isReducedMotion: boolean,
): FormattedAgentActivity | null {
  if (script.length === 0) return null

  if (isReducedMotion) {
    const lastStep = script.at(-1)
    return lastStep?.activity ?? null
  }

  return script[stepIndex]?.activity ?? null
}

function resolveCurrentEvent(
  script: readonly DemoActivityStep[],
  currentEventIndex: number | null,
  isReducedMotion: boolean,
): ConversationTurnEntry | null {
  if (script.length === 0) return null

  if (isReducedMotion) {
    for (let i = script.length - 1; i >= 0; i--) {
      const step = script[i]
      if (step?.eventOverride) return step.eventOverride
    }
    return null
  }

  if (currentEventIndex === null) return null
  return script[currentEventIndex]?.eventOverride ?? null
}
